/**
 * Settings › Integrations — cloud files (Google Drive, OneDrive & SharePoint).
 *
 *  - Your connections: the current user's own grant in the ACTIVE org, per
 *    provider the org has enabled, with the connected account and Disconnect.
 *  - Organization settings (GET/PUT /api/integrations/cloud-files/org-settings):
 *    org admins (can_manage) enable providers (off by default) and restrict
 *    which accounts may connect — email domains, and Microsoft tenant IDs.
 *    Everyone else sees the same settings read-only. Turning a provider off
 *    disconnects everyone in the org, so it asks first.
 *
 * Every query key carries the active org id; switching org refetches.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, Loader2, Lock, X } from 'lucide-react';
import {
  cloudFilesApi,
  CloudConnectProvider,
  CloudOrgSettings,
  CloudOrgSettingsResponse,
} from '@/services/cloudFilesApi';
import { useAppTranslation } from '@/contexts/TranslationContext';
import useActiveOrgId from '@/hooks/useActiveOrgId';
import { toast } from '../../ui/use-toast';
import { Button } from '../../ui/button';
import { Switch } from '../../ui/switch';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../ui/dialog';
import { errorDetailText, errorStatus } from '../errorDetail';
import { ProviderMark } from './CloudIcons';
import { ChipListInput, normalizeDomain, normalizeTenantId } from './ChipListInput';
import { CONNECT_PROVIDER_LABEL, useCloudPolicyMessages } from './connectErrors';
import { cloudOrgSettingsKey, cloudStatusKey, takeCloudConnectError, useCloudConnect, useCloudFilesStatus } from './useCloudConnect';

const PROVIDERS: CloudConnectProvider[] = ['google_drive', 'microsoft_files'];

function toPayload(s: CloudOrgSettingsResponse | CloudOrgSettings): CloudOrgSettings {
  return {
    google_drive: { enabled: !!s.google_drive?.enabled, allowed_domains: [...(s.google_drive?.allowed_domains ?? [])] },
    microsoft_files: {
      enabled: !!s.microsoft_files?.enabled,
      allowed_domains: [...(s.microsoft_files?.allowed_domains ?? [])],
      allowed_tenant_ids: [...(s.microsoft_files?.allowed_tenant_ids ?? [])],
    },
  };
}

export function CloudFilesSettingsPanel({
  showUserConnections = true,
}: {
  /** false where the page already shows the user's own connections (Settings → Connected services). */
  showUserConnections?: boolean;
} = {}) {
  const { t } = useAppTranslation();
  const orgId = useActiveOrgId();
  const queryClient = useQueryClient();
  const { connect, connecting } = useCloudConnect();
  const { connectError, orgName } = useCloudPolicyMessages();
  const status = useCloudFilesStatus();
  const [returnError, setReturnError] = useState<{ title: string; description?: string } | null>(null);

  // ?cloud_error=… from the connect callback (default return is this page).
  useEffect(() => {
    const err = takeCloudConnectError();
    if (!err) return;
    void queryClient
      .fetchQuery({ queryKey: cloudStatusKey(orgId), queryFn: () => cloudFilesApi.getStatus(), staleTime: 0 })
      .catch(() => null)
      .then((fresh) => setReturnError(connectError(err.code, err.provider, fresh)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const orgSettings = useQuery({
    queryKey: cloudOrgSettingsKey(orgId),
    queryFn: () => cloudFilesApi.getOrgSettings(),
    retry: false,
    staleTime: 30_000,
  });

  const [draft, setDraft] = useState<CloudOrgSettings | null>(null);
  useEffect(() => {
    setDraft(orgSettings.data ? toPayload(orgSettings.data) : null);
  }, [orgSettings.data]);
  const [confirmDisable, setConfirmDisable] = useState<CloudConnectProvider[] | null>(null);

  const canManage = !!orgSettings.data?.can_manage;
  const dirty = useMemo(
    () => !!draft && !!orgSettings.data && JSON.stringify(draft) !== JSON.stringify(toPayload(orgSettings.data)),
    [draft, orgSettings.data],
  );

  const save = useMutation({
    mutationFn: (payload: CloudOrgSettings) => cloudFilesApi.updateOrgSettings(payload),
    onSuccess: (result) => {
      const revoked = result?.revoked ?? 0;
      toast({
        title: t('messaging.cloud.settingsSaved', 'Cloud file settings saved'),
        description: revoked
          ? t('messaging.cloud.settingsRevoked', '{{n}} connection(s) were disconnected.', { n: revoked })
          : undefined,
      });
      queryClient.setQueryData(cloudOrgSettingsKey(orgId), { ...result, can_manage: true });
      void queryClient.invalidateQueries({ queryKey: cloudOrgSettingsKey(orgId) });
      void queryClient.invalidateQueries({ queryKey: cloudStatusKey(orgId) });
    },
  });

  const disconnect = useMutation({
    mutationFn: (provider: CloudConnectProvider) => cloudFilesApi.disconnect(provider),
    onSuccess: (_r, provider) => {
      toast({
        title: t('messaging.cloud.disconnected', '{{provider}} disconnected', { provider: CONNECT_PROVIDER_LABEL[provider] }),
      });
      void queryClient.invalidateQueries({ queryKey: cloudStatusKey(orgId) });
    },
    onError: (e, provider) => {
      toast({
        variant: 'destructive',
        title: t('messaging.cloud.disconnectFailed', 'Could not disconnect {{provider}}', { provider: CONNECT_PROVIDER_LABEL[provider] }),
        description: errorDetailText(e) ?? undefined,
      });
    },
  });

  const requestSave = () => {
    if (!draft || !orgSettings.data) return;
    const turningOff = PROVIDERS.filter((p) => orgSettings.data[p]?.enabled && !draft[p].enabled);
    if (turningOff.length) setConfirmDisable(turningOff);
    else save.mutate(draft);
  };

  const setProvider = <K extends CloudConnectProvider>(p: K, patch: Partial<CloudOrgSettings[K]>) =>
    setDraft((d) => (d ? { ...d, [p]: { ...d[p], ...patch } } : d));

  const enabledProviders = PROVIDERS.filter((p) => status.data?.[p]?.org_enabled);
  const orgSectionHidden = orgSettings.isError && errorStatus(orgSettings.error) === 403;

  return (
    <section className="space-y-6" data-testid="cloud-files-settings">
      <div>
        <h2 className="text-lg font-semibold">{t('messaging.cloud.settingsTitle', 'Cloud files')}</h2>
        <p className="text-sm text-muted-foreground">
          {t('messaging.cloud.settingsHint', 'Attach Google Drive, OneDrive and SharePoint files to messages as links.')}
        </p>
      </div>

      {returnError && (
        <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm" role="alert">
          <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-destructive" />
          <div className="flex-1">
            <p className="font-medium text-destructive">{returnError.title}</p>
            {returnError.description && <p className="text-muted-foreground">{returnError.description}</p>}
          </div>
          <button
            type="button"
            onClick={() => setReturnError(null)}
            aria-label={t('messaging.common.dismiss', 'Dismiss')}
            className="text-muted-foreground hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Your connections */}
      {showUserConnections && (
        <div className="rounded-lg border p-4 dark:border-slate-700">
          <h3 className="mb-3 font-medium">{t('messaging.cloud.yourConnections', 'Your connections')}</h3>
          {status.isLoading && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
          {status.isError && (
            <p className="text-sm text-destructive">
              {errorDetailText(status.error) || t('messaging.cloud.statusFailed', 'Could not check your cloud file connections')}
            </p>
          )}
          {status.data && enabledProviders.length === 0 && (
            <p className="text-sm text-muted-foreground" data-testid="cloud-none-enabled">
              {t('messaging.cloud.noneEnabled', "{{org}} hasn't turned on cloud files yet.", { org: orgName })}
            </p>
          )}
          <ul className="space-y-3">
            {status.data &&
              enabledProviders.map((p) => {
                const st = status.data![p];
                return (
                  <li key={p} className="flex flex-wrap items-center gap-3" data-testid={`cloud-connection-${p}`}>
                    <ProviderMark provider={p === 'google_drive' ? 'google_drive' : 'onedrive'} className="h-5 w-5" />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium">{CONNECT_PROVIDER_LABEL[p]}</div>
                      <div className="truncate text-xs text-muted-foreground">
                        {!st.available
                          ? t('messaging.cloud.notConfiguredShort', 'Not configured for this workspace')
                          : st.connected
                            ? t('messaging.cloud.connectedAs', 'Connected as {{email}}', { email: st.account_email || '—' })
                            : t('messaging.cloud.notConnected', 'Not connected')}
                      </div>
                    </div>
                    {st.available &&
                      (st.connected ? (
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={disconnect.isPending}
                          onClick={() => disconnect.mutate(p)}
                        >
                          {disconnect.isPending && disconnect.variables === p && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
                          {t('messaging.cloud.disconnect', 'Disconnect')}
                        </Button>
                      ) : (
                        <Button size="sm" disabled={connecting === p} onClick={() => void connect(p, st.connect_path)}>
                          {connecting === p && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
                          {t('messaging.cloud.connect', 'Connect')}
                        </Button>
                      ))}
                  </li>
                );
              })}
          </ul>
        </div>
      )}

      {/* Organization settings */}
      {!orgSectionHidden && (
        <div className="rounded-lg border p-4 dark:border-slate-700" data-testid="cloud-org-settings">
          <div className="mb-3 flex items-center gap-2">
            <h3 className="font-medium">{t('messaging.cloud.orgSettings', 'Organization settings for {{org}}', { org: orgName })}</h3>
            {orgSettings.data && !canManage && <Lock className="h-4 w-4 text-muted-foreground" aria-hidden="true" />}
          </div>
          {orgSettings.isLoading && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
          {orgSettings.isError && (
            <p className="text-sm text-destructive">
              {errorDetailText(orgSettings.error) || t('messaging.cloud.orgSettingsFailed', 'Could not load organization settings.')}
            </p>
          )}
          {orgSettings.data && !canManage && (
            <p className="mb-3 text-xs text-muted-foreground" data-testid="cloud-org-readonly">
              {t('messaging.cloud.readOnly', 'Only organization admins can change these settings.')}
            </p>
          )}
          {draft && (
            <div className="space-y-5">
              {PROVIDERS.map((p) => (
                <div key={p} className="space-y-3 border-t pt-4 first:border-t-0 first:pt-0 dark:border-slate-700">
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2">
                      <ProviderMark provider={p === 'google_drive' ? 'google_drive' : 'onedrive'} className="h-5 w-5" />
                      <span className="text-sm font-medium">{CONNECT_PROVIDER_LABEL[p]}</span>
                    </div>
                    {canManage ? (
                      <Switch
                        checked={draft[p].enabled}
                        onCheckedChange={(v: boolean) => setProvider(p, { enabled: !!v } as any)}
                        aria-label={t('messaging.cloud.enableProvider', 'Enable {{provider}}', { provider: CONNECT_PROVIDER_LABEL[p] })}
                      />
                    ) : (
                      <span className="text-xs text-muted-foreground">
                        {draft[p].enabled ? t('messaging.cloud.on', 'On') : t('messaging.cloud.off', 'Off')}
                      </span>
                    )}
                  </div>
                  <ChipListInput
                    testId={`cloud-domains-${p}`}
                    label={t('messaging.cloud.allowedDomains', 'Allowed email domains')}
                    values={draft[p].allowed_domains}
                    onChange={(v) => setProvider(p, { allowed_domains: v } as any)}
                    normalize={normalizeDomain}
                    invalidMessage={t('messaging.cloud.invalidDomain', 'Not a valid domain')}
                    placeholder={t('messaging.cloud.domainPlaceholder', 'example.org — leave empty to allow any account')}
                    emptyText={t('messaging.cloud.anyAccount', 'Any account')}
                    readOnly={!canManage}
                  />
                  {p === 'microsoft_files' && (
                    <ChipListInput
                      testId="cloud-tenants"
                      label={t('messaging.cloud.allowedTenants', 'Allowed Microsoft 365 tenant IDs')}
                      values={draft.microsoft_files.allowed_tenant_ids}
                      onChange={(v) => setProvider('microsoft_files', { allowed_tenant_ids: v })}
                      normalize={normalizeTenantId}
                      invalidMessage={t('messaging.cloud.invalidTenant', 'Not a valid tenant ID (GUID)')}
                      placeholder="00000000-0000-0000-0000-000000000000"
                      emptyText={t('messaging.cloud.anyTenant', 'Any tenant')}
                      readOnly={!canManage}
                    />
                  )}
                </div>
              ))}
              {save.isError && (
                <p className="text-sm text-destructive" role="alert">
                  {errorDetailText(save.error) || t('messaging.cloud.saveFailed', 'Could not save the settings.')}
                </p>
              )}
              {canManage && (
                <div className="flex justify-end gap-2">
                  <Button
                    variant="outline"
                    disabled={!dirty || save.isPending}
                    onClick={() => orgSettings.data && setDraft(toPayload(orgSettings.data))}
                  >
                    {t('messaging.channel.cancel', 'Cancel')}
                  </Button>
                  <Button disabled={!dirty || save.isPending} onClick={requestSave}>
                    {save.isPending && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
                    {t('messaging.cloud.save', 'Save')}
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <Dialog open={!!confirmDisable} onOpenChange={(o) => !o && setConfirmDisable(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t('messaging.cloud.disableTitle', 'Turn off cloud files?')}</DialogTitle>
            <DialogDescription>
              {(confirmDisable ?? []).map((p) => (
                <span key={p} className="block">
                  {t('messaging.cloud.disableConfirm', 'This disconnects everyone in {{org}} from {{provider}}.', {
                    org: orgName,
                    provider: CONNECT_PROVIDER_LABEL[p],
                  })}
                </span>
              ))}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDisable(null)}>
              {t('messaging.channel.cancel', 'Cancel')}
            </Button>
            <Button
              variant="danger"
              onClick={() => {
                setConfirmDisable(null);
                if (draft) save.mutate(draft);
              }}
            >
              {t('messaging.cloud.disableAction', 'Turn off and disconnect')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}

export default CloudFilesSettingsPanel;
